import React, { useState, useEffect } from 'react';
import { Input, Button, Space, Table, Popconfirm, Tooltip, Modal, Tag, Pagination } from 'antd';
import { DeleteOutlined, SearchOutlined, ExclamationCircleOutlined, EditOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { connect } from 'react-redux';
import { Actions } from '../../store/actions';
import SliderImageEditForm from './SliderImageEditForm';
import ReactImage from '../../components/Image';

const SliderImageManagement = ({ sliderImages, fetchSliderImages, updateSliderImageStatus, deleteSliderImage }) => {
	const [searchText, setSearchText] = useState('');
	const [searchedColumn, setSearchedColumn] = useState('');
	const [editMobileModal, setEditMobileModal] = useState(false);
	const [editWebModal, setEditWebModal] = useState(false);
	const [editSliderImage, setEditSliderImage] = useState();

	useEffect(() => {
		fetchSliderImages();
	}, []);

	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: function filterDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
			return (
				<div style={{ padding: 8 }}>
					<Input
						placeholder={`Search ${dataIndex}`}
						value={selectedKeys[0]}
						onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
						style={{ width: 188, marginBottom: 8, display: 'block' }}
					/>
					<Space>
						<Button
							type="primary"
							onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 90 }}
						>
							Search
						</Button>
						<Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
							Reset
						</Button>
					</Space>
				</div>
			);
		},
		filterIcon: function filter(filtered) {
			return <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />;
		},
		onFilter: (value, record) =>
			record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
		render: function render(text) {
			return searchedColumn === dataIndex ? (
				<Highlighter
					highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
					searchWords={[searchText]}
					autoEscape
					textToHighlight={text ? text.toString() : ''}
				/>
			) : (
				text
			);
		},
	});

	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();
		setSearchText(selectedKeys[0]);
		setSearchedColumn(dataIndex);
	};

	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText('');
	};

	const onDelete = async (id) => {
		await deleteSliderImage(id);
		fetchSliderImages();
	};

	const columns = [
		{
			title: 'Web Image',
			dataIndex: 'webImage',
			key: 'webImage',
			render: function image(text, record) {
				return (
					<Space size="middle">
						<ReactImage src={record.webImage} width={50} />
						<Tooltip title="Edit Slider Web Image">
							<EditOutlined
								onClick={() => {
									setEditSliderImage(record);
									setEditWebModal(!editWebModal);
								}}
							/>
						</Tooltip>
					</Space>
				);
			},
		},
		{
			title: 'Mobile Image',
			dataIndex: 'mobileImage',
			key: 'mobileImage',
			render: function image(text, record) {
				return (
					<Space size="middle">
						<ReactImage src={record.mobileImage} width={50} />
						<Tooltip title="Edit Slider Mobile Image">
							<EditOutlined
								onClick={() => {
									setEditSliderImage(record);
									setEditMobileModal(!editMobileModal);
								}}
							/>
						</Tooltip>
					</Space>
				);
			},
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: function status(text, record) {
				return (
					<Space size="middle">
						{record.status === 0 ? 'Inactive' : 'Active'}
						<Tooltip title={`Change Status of Image to ${record.status === 0 ? 'Active' : 'Inactive'}`}>
							<EditOutlined
								onClick={() => updateSliderImageStatus(record._id, { status: record.status === 0 ? 1 : 0 })}
							/>
						</Tooltip>
					</Space>
				);
			},
		},
		{
			title: 'Priority',
			dataIndex: 'priority',
			key: 'priority',
			render: function status(text, record) {
				return <Space size="middle">{record?.priority}</Space>;
			},
		},
		{
			title: 'Action',
			key: 'action',
			render: function action(text, record) {
				return (
					<Space size="middle">
						<Popconfirm
							title="Are you sure to delete this Slider Image?"
							onConfirm={() => onDelete(record._id)}
							okText="Yes"
							cancelText="No"
						>
							<DeleteOutlined />
						</Popconfirm>
					</Space>
				);
			},
		},
	];

	return (
		<div>
			<Table
				dataSource={sliderImages}
				columns={columns}
				rowKey={(record) => record._id}
				size="small"
				scroll={{ x: true }}
			/>
			<Modal
				title={'Edit Mobile Slider Image'}
				centered={true}
				visible={editMobileModal}
				footer={null}
				onCancel={() => setEditMobileModal(false)}
				destroyOnClose={true}
			>
				<SliderImageEditForm
					isEditForWeb={false}
					sliderImage={editSliderImage}
					closeDialog={() => setEditMobileModal(false)}
				/>
			</Modal>
			<Modal
				title={'Edit Web Slider Image'}
				centered={true}
				visible={editWebModal}
				footer={null}
				onCancel={() => setEditWebModal(false)}
				destroyOnClose={true}
			>
				<SliderImageEditForm
					isEditForWeb={true}
					sliderImage={editSliderImage}
					closeDialog={() => setEditWebModal(false)}
				/>
			</Modal>
		</div>
	);
};

const mapStateToProps = (store) => ({
	sliderImages: store.sliderImage.sliderImages,
});

const mapDispatchToProps = (dispatch) => ({
	fetchSliderImages: () => dispatch(Actions.fetchSliderImages()),
	updateSliderImageStatus: (id, body) => dispatch(Actions.updateSliderImageStatus(id, body)),
	deleteSliderImage: (id) => dispatch(Actions.deleteSliderImage(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SliderImageManagement);
